import React, { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  AssessmentAnswers,
  BUDGET_OPTIONS,
  GOAL_OPTIONS,
  REGION_OPTIONS,
  TIMELINE_OPTIONS,
  BudgetKey,
  GoalKey,
  RegionKey,
  TimelineKey,
  matchPrograms,
  toSerializableMatches,
} from "../../data/assessment";
import {
  iconFlagUK,
  iconFlagGreece,
  iconFlagMalta,
  iconFlagPortugal,
  iconFlagUAE,
  iconFlagSpain,
  iconFlagCyprus,
  iconFlagHungary,
  iconFlagTurkey,
  iconFlagPanama,
  iconFlagSingapore,
  iconFlagJapan,
  iconFlagStKitts,
  iconFlagGrenada,
  iconFlagAntigua,
  iconFlagDominica,
  iconFlagStLucia,
  iconFlagVanuatu,
  iconFlagGeorgia,
  iconFlagSaoTome,
  iconFlagNauru,
} from "../../assets/imgExport";

const watermarkFlags = [
  iconFlagUK,
  iconFlagGreece,
  iconFlagMalta,
  iconFlagPortugal,
  iconFlagUAE,
  iconFlagSpain,
  iconFlagCyprus,
  iconFlagHungary,
  iconFlagTurkey,
  iconFlagPanama,
  iconFlagSingapore,
  iconFlagJapan,
  iconFlagStKitts,
  iconFlagGrenada,
  iconFlagAntigua,
  iconFlagDominica,
  iconFlagStLucia,
  iconFlagVanuatu,
  iconFlagGeorgia,
  iconFlagSaoTome,
  iconFlagNauru,
];

type StepKey = "budget" | "goal" | "region" | "timeline";
const STEPS: StepKey[] = ["budget", "goal", "region", "timeline"];

export default function AssessmentPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const preset = (location.state as { presetBudget?: BudgetKey } | null) || null;

  const [answers, setAnswers] = useState<Partial<AssessmentAnswers>>(
    preset?.presetBudget ? { budget: preset.presetBudget } : {},
  );
  const [stepIndex, setStepIndex] = useState(preset?.presetBudget ? 1 : 0);
  const [analyzing, setAnalyzing] = useState(false);

  const tiledFlags = useMemo(
    () => [...watermarkFlags, ...watermarkFlags, ...watermarkFlags],
    [],
  );

  const currentStep = STEPS[stepIndex];
  const progress = Math.round(((stepIndex + (analyzing ? 1 : 0)) / STEPS.length) * 100);

  function selectAnswer<K extends keyof AssessmentAnswers>(key: K, value: AssessmentAnswers[K]) {
    const next = { ...answers, [key]: value };
    setAnswers(next);

    if (stepIndex < STEPS.length - 1) {
      setStepIndex(stepIndex + 1);
      return;
    }

    // Last question answered -- run the "AI matching" transition.
    setAnalyzing(true);
    window.setTimeout(() => {
      const finalAnswers = next as AssessmentAnswers;
      const matches = toSerializableMatches(matchPrograms(finalAnswers));
      navigate("/assessment/result", { state: { answers: finalAnswers, matches } });
    }, 1500);
  }

  function goBack() {
    if (stepIndex === 0) {
      navigate(-1);
      return;
    }
    setStepIndex(stepIndex - 1);
  }

  return (
    <div className="relative min-h-screen bg-secondary-dark text-white overflow-hidden flex flex-col">
      <div className="absolute inset-0 flex flex-wrap content-center justify-center opacity-[0.12]">
        {tiledFlags.map((flag, index) => (
          <img
            key={"assessment-flag-" + index}
            className="w-14 h-14 sm:w-16 sm:h-16 object-cover rounded-md m-1 grayscale-[15%] shrink-0"
            style={{ transform: `rotate(${(index % 5) - 2}deg)` }}
            src={flag}
            alt=""
            aria-hidden="true"
          />
        ))}
      </div>
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at center, rgba(35,45,57,0.95) 0%, rgba(35,45,57,0.9) 45%, rgba(35,45,57,0.8) 100%)",
        }}
      />

      <div className="relative z-10 flex-1 flex flex-col items-center px-6 py-16 sm:py-24">
        <div className="w-full max-w-2xl">
          <div className="flex items-center justify-between mb-3">
            <p className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-white/70 border border-white/30 rounded-full px-4 py-1.5">
              AI 智能匹配
            </p>
            <p className="text-xs text-white/50">
              {analyzing ? STEPS.length : stepIndex + 1} / {STEPS.length}
            </p>
          </div>
          <div className="h-1 w-full bg-white/15 rounded-full overflow-hidden mb-10">
            <div
              className="h-full bg-white transition-all duration-500 ease-out"
              style={{ width: `${Math.max(progress, 8)}%` }}
            />
          </div>

          {stepIndex === 0 && !analyzing && (
            <p className="text-xs text-white/45 leading-relaxed mb-6 -mt-4">
              以上结果由 AI 智能匹配系统初步生成，仅供参考；精准的匹配方案仍需顾问一对一进一步咨询确认。
            </p>
          )}

          {analyzing ? (
            <div className="flex flex-col items-center text-center py-20">
              <div className="w-10 h-10 border-2 border-white/30 border-t-white rounded-full animate-spin mb-8" />
              <h2 className="text-2xl font-main-sans text-white">AI 正在分析您的方案匹配…</h2>
              <p className="text-white/60 mt-3">正在比对20多个国家与地区的身份规划方案</p>
            </div>
          ) : (
            <>
              {currentStep === "budget" && (
                <QuestionStep
                  title="您的预算区间大约是？"
                  subtitle="用于匹配投资门槛合适的身份规划方案"
                  options={BUDGET_OPTIONS}
                  onSelect={(key) => selectAnswer("budget", key as BudgetKey)}
                />
              )}
              {currentStep === "goal" && (
                <QuestionStep
                  title="您更希望获得哪种身份？"
                  subtitle="居留身份与海外永居或者护照的路径和权益有所不同"
                  options={GOAL_OPTIONS}
                  onSelect={(key) => selectAnswer("goal", key as GoalKey)}
                />
              )}
              {currentStep === "region" && (
                <QuestionStep
                  title="意向地区是？"
                  subtitle="NVFO 覆盖欧洲、美洲、中东、亚太与非洲的20多个国家与地区"
                  options={REGION_OPTIONS.map((o) => ({ ...o, hint: "" }))}
                  onSelect={(key) => selectAnswer("region", key as RegionKey)}
                />
              )}
              {currentStep === "timeline" && (
                <QuestionStep
                  title="期望的办理时效？"
                  subtitle="用于匹配审批周期相符的方案"
                  options={TIMELINE_OPTIONS}
                  onSelect={(key) => selectAnswer("timeline", key as TimelineKey)}
                />
              )}

              <button
                className="mt-10 text-sm text-white/50 hover:text-white/80"
                onClick={goBack}
              >
                ← 上一步
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function QuestionStep({
  title,
  subtitle,
  options,
  onSelect,
}: {
  title: string;
  subtitle: string;
  options: { key: string; label: string; hint: string }[];
  onSelect: (key: string) => void;
}) {
  return (
    <div>
      <h1 className="text-2xl sm:text-3xl font-main-sans mb-2 text-white">{title}</h1>
      <p className="text-white/60 mb-8">{subtitle}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {options.map((option) => (
          <button
            key={option.key}
            onClick={() => onSelect(option.key)}
            className="text-left border border-white/20 rounded-xl px-6 py-5 hover:border-white hover:bg-white/5 transition-colors"
          >
            <p className="font-medium">{option.label}</p>
            {option.hint && <p className="text-sm text-white/50 mt-1">{option.hint}</p>}
          </button>
        ))}
      </div>
    </div>
  );
}
